import "./style.scss";

import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { Avatar, TableCell, TableRow } from "@mui/material";
import { getAgreementRelatedResourcePath } from "components/AgreementValidationModal/utils";
import ControlFilter from "components/shared/ControlFilter";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableTitleComponent from "components/shared/ManagementTableTitleComponent";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import { CREALO_SIGNATORY_REFUSED } from "config/docoonEventTypes";
import { PortalType } from "contexts/portal.context";
import { format } from "date-fns";
import { Body, PageTitle, SmallBody, TinyBody } from "helpers/TypographyComponents";
import { isAgreementOfTerm } from "helpers/termAgreements";
import { formatLocaleDateString, getInitials } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { Agreement, DocoonEvent } from "store/agreementsStore";
import {
    AgreementSignatureStatus,
    AgreementStatus,
    fetchAgreements,
    resetAgreementCommentsStatus,
    resetAgreementStatus,
    resetNavigateAfterSendingAg,
    resendToDocoon,
} from "store/agreementsStore";
import { resetArticle } from "store/articleStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { resetTermStatus } from "store/termStore";
import { EPortalType } from "store/types";
import type { IUser } from "types";

import Plus from "assets/img/svg/plus";
import ButtonMenu from "components/shared/ButtonMenu";
import DocoonTrackingModal from "components/shared/DocoonTrackingModal";
import ModalBase from "components/shared/ModalBase";
import { Spinner } from "components/shared/Spinner";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import { AgreementTableAction, AgreementTableSignatories, AgreementTableValidators } from "./FormatColumnValues";

export type Validator = { order: number; status: string; userId: number };

export type AgreementsValidatorsStatus = "approve" | "decline" | "idle" | "waiting";

export const agreementStatusRules = (): Record<AgreementStatus, IStatusRule> => ({
    [AgreementStatus.DRAFT]: {
        color: "yellow",
        text: t(`ContractValidation.Status${AgreementStatus.DRAFT}`),
    },
    [AgreementStatus.TRASHED]: {
        color: "grey",
        text: t(`ContractValidation.Status${AgreementStatus.TRASHED}`),
    },
    [AgreementStatus.NEGOTIATING]: {
        color: "blue",
        text: t(`ContractValidation.Status${AgreementStatus.NEGOTIATING}`),
    },
    [AgreementStatus.SENT]: {
        color: "orange",
        text: t(`ContractValidation.Status${AgreementStatus.SENT}`),
    },
    [AgreementStatus.SIGNED]: {
        color: "green",
        text: t(`ContractValidation.SignedOn`),
    },
});

const ContractValidation = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { agreements, agreementSigners } = useAppSelector((state) => state.agreements);
    const { agreementsStatus, agreementSignersStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const { userData } = useAppSelector((state) => state.settings);
    const { getCompanySettings } = useCompanySettings();
    const showPublicIdColumn =
        !!getCompanySettings("display_agreement_public_id") && !!getCompanySettings("uses_agreements");
    const userList = useNormalizedCompanyUsers();
    const [filterInput, setFilterInput] = useState("");
    const [control, setControl] = useState<string | undefined>();
    const [typeFilter, setTypeFilter] = useState<string | undefined>();
    const [searchParams, setSearchParams] = useSearchParams();
    const resourceFilter = useMemo(() => {
        const termId = searchParams.get("termId");
        const articleId = searchParams.get("articleId");
        const frameworkContractId = searchParams.get("frameworkContractId");
        const beneficiaryIdParam = searchParams.get("beneficiaryId");
        const titleIdParam = searchParams.get("titleId");
        const workIdParam = searchParams.get("workId");
        if (termId)
            return {
                relatedResource: "term",
                relatedResourceId: Number(termId),
                parentId: beneficiaryIdParam ? Number(beneficiaryIdParam) : null,
                titleId: titleIdParam ? Number(titleIdParam) : null,
                workId: workIdParam ? Number(workIdParam) : null,
            } as const;
        if (articleId)
            return {
                relatedResource: "article",
                relatedResourceId: Number(articleId),
                parentId: titleIdParam ? Number(titleIdParam) : null,
            } as const;
        if (frameworkContractId)
            return {
                relatedResource: "frameworkContract",
                relatedResourceId: Number(frameworkContractId),
                parentId: beneficiaryIdParam ? Number(beneficiaryIdParam) : null,
            } as const;
        return null;
    }, [searchParams]);
    const clearResourceFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("termId");
        next.delete("articleId");
        next.delete("frameworkContractId");
        next.delete("beneficiaryId");
        next.delete("titleId");
        next.delete("workId");
        setSearchParams(next, { replace: true });
    };
    const [membersById, setMembersById] = useState<{ [id: string]: IUser }>({});
    const [trackingAgreementId, setTrackingAgreementId] = useState<number | null>(null);
    const [trackingAgreementTitle, setTrackingAgreementTitle] = useState("");
    const [trackingAgreementFilename, setTrackingAgreementFilename] = useState("");
    const [refusalOpenId, setRefusalOpenId] = useState<number | null>(null);
    const [refusalEventsCache, setRefusalEventsCache] = useState<Record<number, DocoonEvent[]>>({});
    const [refusalLoadingId, setRefusalLoadingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);
    const [confirmResendAgreement, setConfirmResendAgreement] = useState<Agreement | null>(null);
    const portalType = useContext(PortalType);
    const activeSignProvider = useActiveSignProvider();

    const handleCreateAgreement = (documentType: "agreement" | "avenant" | "framework_contract") => {
        const state: NewAgreementLocationState = {
            modalType: "single",
            documentType,
            signatureProvider: activeSignProvider.key,
        };
        navigate("/agreements/new", { state });
    };

    const groupMembersById = (res) => {
        // The rejected case already toasts; its payload is not a member list.
        if (!getUserListByCompany.fulfilled.match(res)) return;
        const membersById = res.payload.reduce((acc: { [id: string]: IUser }, member: IUser) => {
            acc[member.id] = member;
            return acc;
        }, {});
        setMembersById(membersById);
    };

    useEffect(() => {
        dispatch(fetchAgreements());
        dispatch(getUserListByCompany()).then(groupMembersById);
    }, []);

    const handleToggleRefusal = (agreementId: number) => {
        if (refusalOpenId === agreementId) {
            setRefusalOpenId(null);
            return;
        }
        setRefusalOpenId(agreementId);
        if (!refusalEventsCache[agreementId]) {
            setRefusalLoadingId(agreementId);
            axios
                .get(import.meta.env.REACT_APP_API_ENDPOINT + `/agreements/${agreementId}/docoon-events`)
                .then((res) => {
                    setRefusalEventsCache((prev) => ({ ...prev, [agreementId]: res.data.data?.events ?? [] }));
                })
                .catch(() => {
                    setRefusalEventsCache((prev) => ({ ...prev, [agreementId]: [] }));
                })
                .finally(() => setRefusalLoadingId(null));
        }
    };

    const handleResendAsIs = async (agreementId: number) => {
        setResendingId(agreementId);
        setConfirmResendAgreement(null);
        const toastId = toast.loading(t("ContractValidation.ResendInProgress") as string);
        try {
            await dispatch(resendToDocoon({ agreementId })).unwrap();
            toast.update(toastId, {
                render: t("ContractValidation.ResendSuccess") as string,
                type: "success",
                isLoading: false,
                autoClose: 4000,
            });
            setRefusalOpenId(null);
            dispatch(fetchAgreements());
        } catch (err: any) {
            const msgKey: string | undefined = err?.message;
            const translated = msgKey ? t(msgKey, { defaultValue: "" }) : "";
            toast.update(toastId, {
                render: translated || (t("ContractValidation.ResendError") as string),
                type: "error",
                isLoading: false,
                autoClose: 4000,
            });
        } finally {
            setResendingId(null);
        }
    };

    /**
     * Handle open (navigate) to an agreement
     * @param id agreement id
     * @param titleId related title id
     */
    const handleOpenAgreement = (id: number) => {
        // Force reset fetch term status to avoid blink Skeleton
        dispatch(resetTermStatus());
        dispatch(resetAgreementStatus());
        dispatch(resetAgreementCommentsStatus());
        dispatch(resetNavigateAfterSendingAg());
        dispatch(resetArticle());
        navigate(`/agreements/${id}`);
    };

    const contractColumns: LastElementTableColumns[] = [
        {
            id: "relatedResourceName",
            label: t(`ContractValidation.RelatedResource_${portalType}`),
            subtitle: "title",
            align: "left",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value, _, currentRow: Agreement) => {
                const { isRelatedResourceDeleted, titleId, workId, relatedResource } = currentRow;
                const isFrameworkContract = relatedResource === "frameworkContract";
                const relatedResourcePath = isFrameworkContract
                    ? undefined
                    : getAgreementRelatedResourcePath(portalType, { workId, titleId });
                return (
                    <ManagementTableTitleComponent
                        title={!isRelatedResourceDeleted ? value : ""}
                        titleCoverUrl={currentRow?.titleAvatarUrl ?? ""}
                        isbn={currentRow?.legacyTermTitleEan13}
                        nav={
                            relatedResourcePath
                                ? () =>
                                      navigate(
                                          relatedResourcePath,
                                          workId != null ? { state: { filterList: true } } : undefined,
                                      )
                                : undefined
                        }
                        handleQuery={() => setFilterInput(value)}
                    />
                );
            },
        },
        {
            id: "type",
            label: t("ContractValidation.TypeDocumentLabel"),
            align: "left",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value, _, currentRow: Agreement) => {
                const isFramework = currentRow.relatedResource === "frameworkContract";
                const isAvenant = value === "avenant";
                const chipClass = isAvenant ? "avenant" : isFramework ? "framework_contract" : "";
                const label = isAvenant
                    ? t("ContractSettings.TypeAvenant")
                    : isFramework
                      ? t("ContractSettings.TypeFrameworkContract")
                      : t("ContractSettings.TypeAgreement");
                return <span className={`agreement-type-chip ${chipClass}`}>{label}</span>;
            },
        },
        ...(showPublicIdColumn
            ? [
                  {
                      id: "publicId",
                      label: t("ContractValidation.PublicId"),
                      align: "left" as const,
                      opacityReducer: "isRelatedResourceDeleted",
                      format: (value: string | null | undefined) => value ?? "-",
                  },
              ]
            : []),
        {
            id: "beneficiary",
            label: t("ContractValidation.Signatory"),
            align: "left",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value, _, currentRow: Agreement) => (
                <AgreementTableSignatories
                    agreement={currentRow}
                    agreementSigners={agreementSigners}
                    agreementSignersStatus={agreementSignersStatus}
                    userList={userList}
                />
            ),
        },
        {
            id: "created",
            label: t("ContractValidation.CreationDate"),
            align: "left",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "status",
            label: t("ContractValidation.Status"),
            align: "left",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value, _, currentRow) => {
                const isSigned = currentRow.signedStatus === AgreementSignatureStatus.SIGNED;
                const isRefused = currentRow.signedStatus === AgreementSignatureStatus.REFUSED;
                const statusRules = {
                    ...agreementStatusRules(),
                    refused: { color: "red", text: t("ContractValidation.StatusRefused") },
                };
                return (
                    <StatusChip
                        status={isSigned ? "signed" : isRefused ? "refused" : value}
                        statusRules={statusRules}
                        extraText={
                            isSigned
                                ? formatLocaleDateString({
                                      date: currentRow.lastUpdate,
                                      options: { numeric: true, timeZone: "UTC" },
                                  })
                                : ""
                        }
                    />
                );
            },
        },
        {
            id: "validators",
            label: t("ContractValidation.Validators"),
            align: "left",
            minWidth: "200px",
            opacityReducer: "isRelatedResourceDeleted",
            format: (value: Validator[]) => <AgreementTableValidators validators={value} membersById={membersById} />,
        },
        {
            id: "action",
            label: "",
            align: "right",
            opacityReducer: "isRelatedResourceDeleted",
            format: (_, id, row: Agreement & { _refusalOpen?: boolean }) => (
                <AgreementTableAction
                    handleOpenAgreement={() => handleOpenAgreement(id as number)}
                    agreement={row}
                    userData={userData}
                    onOpenTracking={(title, filename) => {
                        setTrackingAgreementId(id as number);
                        setTrackingAgreementTitle(title);
                        setTrackingAgreementFilename(filename);
                    }}
                    onToggleRefusal={() => handleToggleRefusal(id as number)}
                    isRefusalOpen={!!row._refusalOpen}
                />
            ),
        },
    ];

    const controlFilter = [
        { value: undefined, label: t("ContractValidation.All") },
        { value: "draft", label: t("ContractValidation.Draft") },
        { value: "negotiating", label: t("ContractValidation.Negotiating") },
        { value: "sent", label: t("ContractValidation.Sent") },
        { value: "has-to-sign", label: t("ContractValidation.ToSignMe") },
        { value: "signed", label: t("ContractValidation.StatusSigned") },
        { value: "refused", label: t("ContractValidation.StatusRefused") },
    ];

    const typeControlFilter = [
        { value: undefined, label: t("ContractValidation.All") },
        { value: "agreement", label: t("ContractValidation.Agreements") },
        { value: "framework_contract", label: t("ContractValidation.FrameworkContracts") },
        { value: "avenant", label: t("ContractValidation.Avenants") },
    ];

    const filteredContractData = useMemo(() => {
        return agreements
            .filter((item) => {
                let controlFilterCondition = control != undefined ? item.status == control : true;
                if (control === "sent") {
                    controlFilterCondition =
                        item.status === "sent" && item.signedStatus === AgreementSignatureStatus.SENT;
                }
                if (control === "has-to-sign") {
                    controlFilterCondition = !!item.mySignatureUrl;
                }
                if (control === "signed") {
                    controlFilterCondition = item.signedStatus === AgreementSignatureStatus.SIGNED;
                }
                if (control === "refused") {
                    controlFilterCondition = item.signedStatus === AgreementSignatureStatus.REFUSED;
                }
                const filterInputCondition =
                    item.relatedResourceName?.toLowerCase().includes(filterInput.toLowerCase()) ||
                    item.beneficiary?.toLowerCase().includes(filterInput.toLowerCase());
                const resourceCondition = !resourceFilter
                    ? true
                    : resourceFilter.relatedResource === "term"
                      ? isAgreementOfTerm(item, {
                            termId: resourceFilter.relatedResourceId,
                            workId: resourceFilter.workId,
                            titleId: resourceFilter.titleId,
                            beneficiaryId: resourceFilter.parentId,
                        })
                      : item.relatedResource === resourceFilter.relatedResource &&
                        +item.relatedResourceId === resourceFilter.relatedResourceId;
                // Framework contracts are stored with beneficiary_agreements.type='agreement'
                // (that column only knows agreement|avenant). The distinction lives on
                // relatedResource='frameworkContract'.
                let typeCondition = true;
                if (typeFilter === "framework_contract") {
                    typeCondition = item.relatedResource === "frameworkContract" && item.type !== "avenant";
                } else if (typeFilter === "agreement") {
                    typeCondition = item.type === "agreement" && item.relatedResource !== "frameworkContract";
                } else if (typeFilter !== undefined) {
                    typeCondition = item.type === typeFilter;
                }
                return controlFilterCondition && filterInputCondition && typeCondition && resourceCondition;
            })
            .sort((a: any, b: any) => new Date(b.created).getTime() - new Date(a.created).getTime())
            .sort((a: any, b) =>
                a.status == AgreementStatus.NEGOTIATING &&
                a.validators.find((item) => item.status == "waiting")?.userId == userData?.id
                    ? -1
                    : 1,
            );
    }, [agreements, filterInput, control, typeFilter, resourceFilter]);

    const dataWithRefusal = useMemo(
        () => filteredContractData.map((a) => ({ ...a, _refusalOpen: a.id === refusalOpenId })),
        [filteredContractData, refusalOpenId],
    );

    const separatorContent = (contract: any, i: number) => {
        return (
            <TableRow>
                <TableCell style={{ padding: 0, border: "none" }} colSpan={6}>
                    <div className="bpstatement-newperiod-separator">
                        {i === 0 ? (
                            <span>{t("ContractValidation.NextValidators")}</span>
                        ) : (
                            <span>{t("ContractValidation.OtherContracts")}</span>
                        )}
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    const isSeparator = (contract: any, index: number, contracts: any) => {
        const previousContract = contracts[index - 1] ?? {};
        if (
            index === 0 &&
            contract.status == AgreementStatus.NEGOTIATING &&
            contract?.validators.find((item) => item.status == "waiting")?.userId == userData?.id
        )
            return true;
        if (
            previousContract.status == AgreementStatus.NEGOTIATING &&
            previousContract?.validators.find((item) => item.status == "waiting")?.userId == userData?.id
        ) {
            return true;
        } else return false;
    };

    const handleBackToResource = () => {
        if (!resourceFilter) return;
        const parent =
            resourceFilter.parentId ??
            (resourceFilter.relatedResource === "article"
                ? filteredContractData[0]?.titleId
                : filteredContractData[0]?.beneficiaryId);
        if (!parent) return;
        if (resourceFilter.relatedResource === "term") {
            navigate(`/beneficiars/${parent}`, {
                state: { tab: "2", termId: resourceFilter.relatedResourceId },
            });
            return;
        }
        if (resourceFilter.relatedResource === "frameworkContract") {
            // Book: framework contracts live inside tab "2" via a sub-segment.
            // Press/audiovisual: they have their own tab "6".
            const isBook = portalType === EPortalType.BOOK;
            navigate(`/beneficiars/${parent}`, {
                state: {
                    tab: isBook ? "2" : "6",
                    ...(isBook && { contractsSegment: "framework" }),
                    frameworkContractId: resourceFilter.relatedResourceId,
                },
            });
            return;
        }
        navigate(`/publications/${parent}`, {
            state: { articleId: resourceFilter.relatedResourceId },
        });
    };

    return (
        <div className="contract-validation">
            <div className="contract-validation-header">
                <div className="contract-validation-header-text">
                    <PageTitle>{t("ContractValidation.ContractValidation")}</PageTitle>
                    <Body color="grey">{t("ContractValidation.ContractValidationSubtitle")}</Body>
                </div>
                <div className="contract-validation-header-actions">
                    {resourceFilter && (
                        <StyledButton buttonType="outlined" onClick={handleBackToResource}>
                            <span aria-hidden>←</span>
                            {t(
                                resourceFilter.relatedResource === "term"
                                    ? "ContractValidation.BackToTerm"
                                    : resourceFilter.relatedResource === "frameworkContract"
                                      ? "ContractValidation.BackToFrameworkContract"
                                      : "ContractValidation.BackToArticle",
                            )}
                        </StyledButton>
                    )}
                    <ButtonMenu
                        buttonType="primary"
                        showExpandBtn={false}
                        mainActions={{
                            icon: <Plus />,
                            name: t("ContractValidation.CreateDocumentForSignature"),
                        }}
                        actions={[
                            {
                                name: t(`ContractValidation.CreateAgreementOnTitle_${portalType}`),
                                action: () => handleCreateAgreement("agreement"),
                            },
                            {
                                name: t("ContractValidation.CreateFrameworkContract"),
                                action: () => handleCreateAgreement("framework_contract"),
                            },
                            {
                                name: t("ContractValidation.CreateAvenant"),
                                action: () => handleCreateAgreement("avenant"),
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="contract-validation-body">
                <div className="contract-validation-filters">
                    <div className="contract-validation-filters-left">
                        <SmallSearchBar filter={filterInput} onSearch={(e) => setFilterInput(e.target.value)} />
                        <ControlFilter
                            filterLabel={`${t("ContractValidation.Status")} :`}
                            filterOptions={controlFilter}
                            controlValue={control}
                            onValueChange={(value) => setControl(value)}
                            selector
                        />
                        <ControlFilter
                            filterLabel={`${t("ContractValidation.TypeDocumentLabel")} :`}
                            filterOptions={typeControlFilter}
                            controlValue={typeFilter}
                            onValueChange={(value) => setTypeFilter(value)}
                        />
                        {resourceFilter && (
                            <button
                                type="button"
                                onClick={clearResourceFilter}
                                className="border-management-border-grey text-foreground hover:bg-backgroundZ2 flex items-center gap-1.5 rounded-[3px] border bg-white px-2 py-1 text-xs"
                            >
                                {t("ContractValidation.FilteredByResource", {
                                    label:
                                        filteredContractData[0]?.relatedResourceName ??
                                        `#${resourceFilter.relatedResourceId}`,
                                })}
                                <span aria-hidden>×</span>
                            </button>
                        )}
                    </div>
                    {/* <div className="contract-validation-filters-right"></div> */}
                </div>
                <div className="contract-validation-table">
                    <LastElementTable
                        columns={contractColumns}
                        data={dataWithRefusal}
                        max={-1}
                        onRowClick={(id, _id2, _type, row: Agreement) =>
                            !row.isRelatedResourceDeleted && handleOpenAgreement(id)
                        }
                        isNewDate={(row: any, index: number, data: any) => isSeparator(row, index, data)}
                        dataType={typeFilter === "avenant" ? "avenants" : "contracts"}
                        separatorContent={(contract: any, i: number) => separatorContent(contract, i)}
                        stickyFooter
                        columnsNumber={contractColumns.length}
                        isLoading={agreementsStatus !== "succeeded"}
                        collapseOn={() => "_refusalOpen"}
                        collapseContent={(row: Agreement & { _refusalOpen: boolean }) => {
                            const events = refusalEventsCache[row.id] ?? [];
                            const isLoadingEvents = refusalLoadingId === row.id;
                            const isResending = resendingId === row.id;
                            const refusedEvents = events.filter((e) => e.type === CREALO_SIGNATORY_REFUSED);
                            return (
                                <div style={{ padding: "12px 24px 16px 24px", paddingRight: 0 }}>
                                    <div
                                        className="flex gap-6"
                                        style={{
                                            background: "var(--danger-background)",
                                            border: "1px solid #F2BFCD",
                                            borderLeft: "4px solid #C76B6B",
                                            borderRadius: "var(--border-radius)",
                                            padding: "16px 16px 16px 20px",
                                        }}
                                    >
                                        {isLoadingEvents ? (
                                            <div className="flex flex-1 items-center justify-center py-2">
                                                <Spinner size={32} />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex-1">
                                                    <TinyBody
                                                        color="grey"
                                                        style={{
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.05em",
                                                            marginBottom: "10px",
                                                        }}
                                                    >
                                                        {t("ContractValidation.RefusalReasonTitle")}
                                                    </TinyBody>
                                                    {refusedEvents.length === 0 ? (
                                                        <SmallBody color="grey">—</SmallBody>
                                                    ) : (
                                                        <div className="flex flex-col gap-3">
                                                            {refusedEvents.map((e, i) => (
                                                                <div key={i} className="flex flex-col gap-2">
                                                                    <SmallBody>
                                                                        «&nbsp;{e.refusalComment ?? "—"}&nbsp;»
                                                                    </SmallBody>
                                                                    <div className="flex items-center gap-2">
                                                                        <Avatar
                                                                            sx={{
                                                                                width: 22,
                                                                                height: 22,
                                                                                fontSize: 10,
                                                                                bgcolor: "#eae8f6",
                                                                                color: "#141516",
                                                                            }}
                                                                        >
                                                                            {getInitials(e.signataire ?? "")}
                                                                        </Avatar>
                                                                        <TinyBody color="grey" tag="span">
                                                                            {e.signataire ?? "—"}
                                                                            {e.eventAt && (
                                                                                <>
                                                                                    {" · "}
                                                                                    {format(
                                                                                        new Date(e.eventAt),
                                                                                        "dd/MM/yyyy 'à' HH:mm",
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </TinyBody>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-2" style={{ minWidth: "190px" }}>
                                                    <StyledButton
                                                        fullWidth
                                                        size="small"
                                                        buttonType="primary"
                                                        onClick={() => {
                                                            if (portalType === EPortalType.BOOK) {
                                                                navigate(`/beneficiars/${row.beneficiaryId}`, {
                                                                    state: { tab: "2", termId: row.relatedResourceId },
                                                                });
                                                            } else {
                                                                navigate(`/publications/${row.titleId}`, {
                                                                    state: { articleId: row.relatedResourceId },
                                                                });
                                                            }
                                                        }}
                                                        style={{ justifyContent: "flex-start" }}
                                                        tooltipText={
                                                            t("ContractValidation.CorrectAndResendTooltip") as string
                                                        }
                                                    >
                                                        <DriveFileRenameOutlineOutlinedIcon style={{ fontSize: 16 }} />
                                                        {t("ContractValidation.CorrectAndResend") as string}
                                                    </StyledButton>
                                                    <StyledButton
                                                        fullWidth
                                                        size="small"
                                                        buttonType="secondary"
                                                        onClick={() => setConfirmResendAgreement(row)}
                                                        disabled={isResending}
                                                        style={{ justifyContent: "flex-start" }}
                                                        tooltipText={
                                                            t("ContractValidation.ResendAsIsTooltip") as string
                                                        }
                                                    >
                                                        <>
                                                            <ReplayOutlinedIcon style={{ fontSize: 16 }} />
                                                            {t("ContractValidation.ResendAsIs") as string}
                                                        </>
                                                    </StyledButton>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            </div>
            <DocoonTrackingModal
                open={trackingAgreementId !== null}
                agreementId={trackingAgreementId}
                agreementTitle={trackingAgreementTitle}
                agreementFilename={trackingAgreementFilename}
                onClose={(hasTerminalStatus) => {
                    setTrackingAgreementId(null);
                    if (hasTerminalStatus) dispatch(fetchAgreements());
                }}
            />
            {confirmResendAgreement &&
                (() => {
                    const signatories = [
                        ...new Set(
                            (refusalEventsCache[confirmResendAgreement.id] ?? [])
                                .map((e) => e.signataire)
                                .filter(Boolean) as string[],
                        ),
                    ];
                    return (
                        <ModalBase
                            open
                            onClose={() => !resendingId && setConfirmResendAgreement(null)}
                            modalTitle={t("ContractValidation.ResendAsIsConfirmTitle")}
                        >
                            <div className="flex flex-col gap-4">
                                <SmallBody>{t("ContractValidation.ResendAsIsConfirmBody") as string}</SmallBody>
                                {signatories.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <SmallBody color="grey">
                                            {t("ContractValidation.ResendAsIsConfirmSignatories") as string}
                                        </SmallBody>
                                        {signatories.map((s, i) => (
                                            <div key={i} className="flex items-center gap-2">
                                                <Avatar
                                                    sx={{
                                                        width: 22,
                                                        height: 22,
                                                        fontSize: 10,
                                                        bgcolor: "#eae8f6",
                                                        color: "#141516",
                                                    }}
                                                >
                                                    {getInitials(s)}
                                                </Avatar>
                                                <SmallBody tag="span">{s}</SmallBody>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2 justify-end" style={{ marginTop: "8px" }}>
                                    <StyledButton
                                        buttonType="secondary"
                                        onClick={() => setConfirmResendAgreement(null)}
                                        disabled={!!resendingId}
                                    >
                                        {t("ContractValidation.Cancel") as string}
                                    </StyledButton>
                                    <StyledButton
                                        buttonType="primary"
                                        onClick={() => handleResendAsIs(confirmResendAgreement.id)}
                                        disabled={!!resendingId}
                                    >
                                        {t("ContractValidation.ResendAsIsConfirmBtn") as string}
                                    </StyledButton>
                                </div>
                            </div>
                        </ModalBase>
                    );
                })()}
        </div>
    );
};

export default ContractValidation;
